import ConsumerHeader from '@/components/consumerHeader'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { useMemo, useState, useEffect, useRef, memo } from 'react'
import {
  useParams,
  useNavigate,
  Outlet,
  useLocation,
  Link as RouterLink,
} from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  getWaterTreeHeader,
  getWaterTreeHierarchyMap,
  type WaterTreeHeader,
  type WaterTreeHierarchyNode,
} from '@/services/dashboardService'
import {
  formatTreeSubtitle,
  resolveTreeName,
} from '@/utils/waterTreeHeader'

// Specialized component for Tabs to isolate re-renders and keep Sidebar animation smooth
const AnalyticsTabs = memo(
  ({
    activeTab,
    options,
    onChange,
  }: {
    activeTab: number
    options: any[]
    onChange: (e: any, v: number) => void
  }) => {
    const { mode } = useAppTheme()
    const isDarkMode = mode === 'dark'
    const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
    const [width, setWidth] = useState(0)
    const tabsRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!tabsRef.current) return
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          setWidth(entry.contentRect.width)
        }
      })
      observer.observe(tabsRef.current)
      return () => observer.disconnect()
    }, [])

    return (
      <Box
        ref={tabsRef}
        sx={{
          borderBottom: 1,
          borderColor: isDarkMode ? '#374151' : 'divider',
          marginTop: '4px',
          width: '100%',
        }}
      >
        <Tabs
          key={`tabs-sync-${width}`}
          value={activeTab}
          onChange={onChange}
          aria-label="analytics dashboard tabs"
          sx={{
            direction: 'rtl',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {options.map(option => (
            <Tab
              key={option.value}
              label={option.label}
              value={option.value}
              disableRipple
              sx={{
                outline: 'none !important',
                color: TEXT_COLOR,
                textAlign: 'right',
                fontFamily: 'Rubik, sans-serif',
                fontStyle: 'normal',
                lineHeight: '24px',
                minWidth: 'auto',
                px: 2,
                '&.Mui-selected': {
                  color: TEXT_COLOR,
                  borderBottom: '3px solid #72A4EB',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>
    )
  }
)

// Which zone type the page shows, set by the route element in App.tsx. The
// :consumerId param is a group id for both.
export type AnalyticsDetailsKind = 'tree' | 'group'

function AnalyticsDetailsLayout({ kind }: { kind: AnalyticsDetailsKind }) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { consumerId, siteId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const treeNameParam = new URLSearchParams(location.search).get('treeName')
  const zoneType = kind === 'tree' ? '1' : '2'

  // Name and subtitle come from the zone itself, not the URL: only the
  // /analytics table passes ?treeName=, so arriving from the water-tree map or
  // the notifications button (or refreshing) used to show the raw id. The param
  // is kept as a first-paint hint so the title doesn't flicker.
  const [header, setHeader] = useState<WaterTreeHeader | null>(null)
  const [hierarchy, setHierarchy] = useState<WaterTreeHierarchyNode[]>([])

  useEffect(() => {
    if (!consumerId) return
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return

    let cancelled = false
    setHeader(null)
    setHierarchy([])

    getWaterTreeHeader(accountId, consumerId, zoneType)
      .then(found => {
        if (!cancelled) setHeader(found)
      })
      .catch(err =>
        console.error(
          '[AnalyticsDetailsLayout] header fetch failed:',
          err.response?.data ?? err
        )
      )

    // Supplies the breadcrumb's parent segment. 404s for groups and for trees
    // with no hierarchy, which just leaves the chain at the tree itself.
    if (kind === 'tree') {
      getWaterTreeHierarchyMap(accountId, consumerId)
        .then(nodes => {
          if (!cancelled) setHierarchy(nodes)
        })
        .catch(() => {
          if (!cancelled) setHierarchy([])
        })
    }

    return () => {
      cancelled = true
    }
  }, [consumerId, kind, zoneType])

  const treeName = resolveTreeName(header, treeNameParam, consumerId)
  const subTitle = formatTreeSubtitle(header)

  const options = useMemo(
    () => [
      { label: 'חישובי נתונים', value: 0, route: 'details' },
      { label: 'טבלת צרכנים', value: 1, route: 'AnalyticsConsumerTable' },
      { label: 'מפת עצי מים', value: 2, route: 'WaterTreeMap' },
    ],
    []
  )

  const activeTab = useMemo(() => {
    const index = options.findIndex(option =>
      location.pathname.includes(option.route)
    )
    return index !== -1 ? index : 0
  }, [location.pathname, options])

  const handleChange = (_: unknown, newValue: number) => {
    navigate(
      `/${siteId}/analytics/${kind}/${consumerId}/${options[newValue].route}${location.search}`
    )
  }

  const breadcrumbItems = useMemo(() => {
    const parents = hierarchy.filter(
      node => node.relationship === 'parent' && node.nodeName?.trim()
    )

    return [
      {
        id: consumerId,
        name: treeName,
        zoneType,
      },
      ...parents.map(parent => ({
        id: String(parent.premiseId),
        name: parent.nodeName.trim(),
        zoneType: '1',
      })),
    ].filter(item => item.id && item.name)
  }, [consumerId, hierarchy, treeName, zoneType])

  return (
    <Box
      sx={{
        paddingTop: { xs: 1.5, md: 2 },
        paddingBottom: { xs: 1, md: 1 },
        paddingRight: { xs: 1, md: 2.5 },
        paddingLeft: { xs: 1, md: 2.5 },
        direction: 'rtl',
      }}
    >
      <ConsumerHeader
        titlePrefix="מספק: "
        title={treeName}
        placment="bottom"
        subTitle={subTitle}
        backTo={`/${siteId}/analytics`}
      />

      <AnalyticsTabs
        activeTab={activeTab}
        options={options}
        onChange={handleChange}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mt: 1.5, // Reduced from 2
          mb: 1.5,
          px: 1,
          gap: 1, // Tighter gap
        }}
      >
        {activeTab === 2 && breadcrumbItems.length > 0 && (
          <Box
            component="nav"
            aria-label="נתיב מפת עצי מים"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.75,
              color: isDarkMode ? '#BBD7FF' : '#003F61',
              fontSize: '13px',
              fontWeight: 900,
              fontFamily: 'Rubik',
            }}
          >
            {breadcrumbItems.map((item, index) => {
              const search = new URLSearchParams({
                treeName: item.name,
                zoneType: item.zoneType,
              })

              return (
                <Box
                  key={`${item.id}-${index}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  {index > 0 && (
                    <Typography component="span" sx={{ font: 'inherit' }}>
                      &lt;
                    </Typography>
                  )}
                  <Typography
                    component={RouterLink}
                    to={`/${siteId}/analytics/${item.id}/WaterTreeMap?${search.toString()}`}
                    sx={{
                      color: 'inherit',
                      font: 'inherit',
                      textDecoration: 'none',
                      borderBottom: '1px solid transparent',
                      '&:hover': {
                        color: '#72A4EB',
                        borderBottomColor: '#72A4EB',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #72A4EB',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      },
                    }}
                  >
                    {index === 0 ? `מספק: ${item.name}` : item.name}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 0 }}>
        <Outlet context={{ treeName, zoneType }} />
      </Box>
    </Box>
  )
}

export default AnalyticsDetailsLayout
